import {add_error, clear_error} from '../shared/form_errors.js'
import { initializeCkeditor5 } from '../shared/ckeditor5_setup.js'
import { initializeImageCompression, initializeUploadFeedback } from '../shared/image_compression.js'
import moment from "moment";

// add_listeners() runs on both turbo:load and DOMContentLoaded so the initial page load is
// covered even if turbo:load fires before this script finishes registering its listener (a real
// race against the non-deferred application.js pack that boots Turbo). Both can fire for the same
// URL, so trackPageView() tracks the last-reported URL and skips the repeat rather than
// double-count a single page_view in GA. Everything here re-binds to the current DOM on every
// call since Turbo replaces the page body on each visit, leaving any previously-bound elements
// (and their listeners) behind.
document.addEventListener("turbo:load", add_listeners);
document.addEventListener("DOMContentLoaded", add_listeners);

function add_listeners() {
    window.__appJsBooted = true;

    trackPageView();
    initializeForms();
    initializeColorSwatches();
    initializeDateValidation();
    initializeCharacterCounters();
    initializeCkeditor5();
    initializeImageCompression();
    initializeUploadFeedback();
    initializeNavSelects();
    initializeAutoSubmitSelects();
}

// The tab filter on a phone. NavigationHelper used to render this select with an inline onchange,
// which script-src drops (no 'unsafe-inline'), so below md the only filter UI on eight index pages
// did nothing. Bound once per element: add_listeners runs on both turbo:load and DOMContentLoaded,
// and Turbo hands us a fresh select on every visit.
function initializeNavSelects() {
    document.querySelectorAll('select[data-nav-select]').forEach(select => {
        if (select.dataset.navSelectBound) return;
        select.dataset.navSelectBound = 'true';
        select.addEventListener('change', () => {
            if (select.value) window.location.href = select.value;
        });
    });
}

// Same story as the nav select: the driver filter on the driver log carried an inline onchange
// that script-src drops, so picking a driver did nothing.
function initializeAutoSubmitSelects() {
    document.querySelectorAll('select[data-auto-submit]').forEach(select => {
        if (select.dataset.autoSubmitBound) return;
        select.dataset.autoSubmitBound = 'true';
        select.addEventListener('change', () => select.form?.requestSubmit());
    });
}

function trackPageView() {
    if (typeof gtag !== 'function') return;
    if (window.__gaLastTrackedUrl === window.location.href) return;
    window.__gaLastTrackedUrl = window.location.href;
    gtag('event', 'page_view', {
        page_location: window.location.href,
        page_title: document.title,
    });
}

// Store original form data
function initializeForms() {
    document.querySelectorAll('form').forEach(form => {
        const formData = new FormData(form);
        form.dataset.originalData = new URLSearchParams(formData).toString();
    });
}

// Character counter
function initializeCharacterCounters() {
    document.querySelectorAll('.count-text').forEach(element => element.addEventListener('keyup', (event) => {
        const group = event.target.closest('.input-group');
        const counter = group && group.querySelector('.counter');
        if (!counter) return;

        const max_length = parseInt(counter.dataset.max) - counter.textContent.length;
        const cur_count = max_length - event.target.value.length;
        if (cur_count <= 0) {
            counter.style.color = 'red';
        } else {
            counter.style.color = 'lightgray';
        }
        counter.textContent = 'Characters left: ' + String(cur_count);
    }));
}

// Color swatch
function initializeColorSwatches() {
    document.querySelectorAll('[id$=color_swatch]').forEach(select => {
        select.addEventListener('change', function() {
            setSwatchColor(this);
        });
        // Set initial color
        setSwatchColor(select);
    });
}

function setSwatchColor(selectElement) {
    const color = selectElement.value;
    const group = selectElement.closest('.input-group');
    if (!group) return;

    const colorSwatch = group.querySelector('.color-swatch');
    if (colorSwatch) {
        colorSwatch.style.backgroundColor = color;
    }
}

// Date validation
function initializeDateValidation() {
    document.querySelectorAll('input[type=date]').forEach(input => {
        input.addEventListener('blur', function() {
            if (this.value !== '') {
                if (moment(this.value, "YYYY-MM-DD", true).isValid()) {
                    clear_error(this);
                } else {
                    add_error(this, 'Invalid Date');
                }
            }
        });
    });
}

// A confirmation for forms Turbo is told to leave alone. Turbo's own data-turbo-confirm only
// runs for forms Turbo handles, and `data: { turbo: false }` is exactly the opt-out - the Remove
// button on saved payment methods needs it, because removing a card redirects back to a page
// whose Stripe pack mounts its card element at module scope and so only works on a full load.
// `data: { confirm: ... }` was @rails/ujs syntax and nothing imports @rails/ujs, so that button
// detached the card from Stripe on the first click with nothing asked. Delegated and bound once
// to document, like the handler below.
// button_to puts data- attributes on the <button>, not the <form>, so the submitter is checked
// first. Capture phase, so this runs before Turbo's own bubble-phase submit handling rather than
// depending on which pack registered its listener first.
document.addEventListener('submit', (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;

    const message = event.submitter?.dataset?.confirm || event.target.dataset.confirm;
    if (message && !window.confirm(message)) {
        event.preventDefault();
        event.stopPropagation();
    }
}, true);

// AJAX complete handler - bound once to document (not re-bound per add_listeners() call, since
// the event is delegated and re-adding this on every turbo:load would stack up one more listener
// per page visit for the life of the session.
document.addEventListener('ajax:complete', (event) => {
    const request = event.detail[0];
    const msg = request.getResponseHeader("X-Message");
    let alert_type = 'alert-success';

    if (request.getResponseHeader("X-Message-Type")?.indexOf("error") !== -1) {
        alert_type = 'alert-danger';
    }

    if (request.getResponseHeader("X-Message-Type")?.indexOf("keep") !== 0) {
        const flashHook = document.getElementById('flash_hook');
        if (msg && flashHook) {
            flashHook.outerHTML = `<div id='flash_hook'> <p>&nbsp;</p> <div class='row'> <div class='span10 offset1'> <div class='alert ${alert_type}'> <button type='button' class='close' data-bs-dismiss='alert'>&times;</button>${msg}</div> </div> </div> </div>`;
        } else if (!msg && flashHook) {
            flashHook.outerHTML = "<div id='flash_hook'></div>";
        }
    }
});
